export type EnumIdentifier = string | number;

export interface EnumValue<T extends EnumIdentifier> {
    readonly id: T;
    readonly name: string;
}

export interface IEnum<T extends EnumIdentifier> {
    getEnumName(): string;
    getName(val: T): string;
    getValue(val: T): EnumValue<T>;
    getValue(val: string): EnumValue<T> | null;
    getValues(...filter: T[]): EnumValue<T>[];
    getExt(): _.Dictionary<EnumValue<T>>;
    getAllIds(): T[];
}

export interface IStringEnum<T extends EnumIdentifier> extends IEnum<T> {
    is(str: string | T): str is T;
    getNames(): _.Dictionary<string>;
}

export interface IFlagEnum extends IEnum<number> {
    is(val: number): val is number;
    getNames(): _.NumericDictionary<string>;
}

export function Enum<T extends EnumIdentifier>(name: string) {
    const meta = strings.enumEx[name] || {};
    let enumExt: _.Dictionary<EnumValue<T>> = {};
    let idToValue: _.Dictionary<EnumValue<T>> = {};
    const EnumType = class {
        constructor(enumDesc: any) {
            enumExt = _.transform(
                enumDesc,
                (result, value, key) => (result[key] = { id: value, name: meta[key] }),
                {}
            ) as any;
            idToValue = _.transform(enumExt, (result, value, key) => (result[value.id as any] = value), {}) as any;
            _.assign(this, enumDesc);
        }

        public is(str: any): boolean {
            return idToValue[str] != null;
        }

        public getEnumName(): string {
            return name;
        }

        public getName(val: any): string {
            const value = idToValue[val as string];
            if (value) return value.name;
            return undefined;
        }

        public getNames(): _.Dictionary<any> {
            return this.getValues().searchTree(e => e.id as any, e => e.name);
        }

        public getValues(...filter: any[]): EnumValue<T>[] {
            if (filter.length === 0) return _.values(idToValue);
            return _(filter)
                .uniq()
                .map(v => idToValue[v])
                .compact()
                .value();
        }

        public getValue(val: any): EnumValue<T> {
            return idToValue[val] || undefined;
        }

        public getExt(): _.Dictionary<EnumValue<T>> {
            return enumExt;
        }

        public getAllIds(): T[] {
            return _.keys(idToValue) as any;
        }
    };
    return {
        createStringEnum<U>(descEnum: U): IStringEnum<T> & U {
            return new EnumType(descEnum) as any;
        },
        createFlagEnum<U>(descEnum: U): IFlagEnum & U {
            return new EnumType(descEnum) as any;
        }
    };
}

export function findEnumValueById<T>(enumType: _.Dictionary<T>, id: any): T {
    const value = _(enumType)
        .values()
        .filter((v: any) => v.id === id)
        .first() as T;
    return value || null;
}

// Return: undefined if not found.
export function findPropertyById(enumObj, id) {
    id = id[0].toLowerCase() + id.substr(1, id.length - 1);
    const value = enumObj[id];
    // didn't find because it could be converted in another way, so let's try to find with case insensitive method
    if (!value) {
        id = id.toLowerCase();
        for (const p in enumObj) if (enumObj.hasOwnProperty(p) && p.toLowerCase() === id) return enumObj[p];
    }
    return value;
}

export function compareEnumValues(value1, value2, enumObject) {
    const values = _.map(enumObject, value => value);
    const index1 = $.inArray(value1, values);
    const index2 = $.inArray(value2, values);
    if (index1 > index2) {
        return 1;
    }
    if (index1 < index2) {
        return -1;
    }
    return 0;
}
